import { CalendarClock, Layers, Package, Repeat } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { FilterChip, Monogram, PlanTag, Pipe, Tabs } from '@/components/redesign/bits'
import { Segmented } from '@/components/ui/segmented'
import { SearchField } from '@/components/ui/search-field'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { errorMessage } from '@/lib/api/errors'
import type { BillingEventStatus, Paginated, SubscriptionRow, SubscriptionState } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, formatMoney, numberFmt } from '@/lib/format'
import { useBillingEvents, useRetryBillingEvent, useSubscriptions } from './api'
import { label } from './format'

const STATES: { key: SubscriptionState; label: string; dot: string; sub: (mrr: number) => string }[] = [
  { key: 'active', label: 'Active', dot: 'ok', sub: (mrr) => `${formatMoney(mrr)} a month` },
  { key: 'past_due', label: 'Payment failing', dot: 'bad', sub: () => 'Card being retried' },
  { key: 'cancelling', label: 'Set to end', dot: 'warn', sub: (mrr) => `${formatMoney(mrr)} a month ends` },
  { key: 'paused', label: 'Paused', dot: '', sub: () => 'Payments on hold' },
  { key: 'on_trial', label: 'On trial', dot: '', sub: () => 'Not paying yet' },
  { key: 'ended', label: 'Ended', dot: 'ink', sub: () => 'Ended or refunded' },
]

const STATE_CHIP: Record<SubscriptionState, { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'ok' },
  past_due: { label: 'Payment failing', tone: 'bad' },
  cancelling: { label: 'Set to end', tone: 'warn' },
  paused: { label: 'Paused', tone: '' },
  on_trial: { label: 'On trial', tone: '' },
  ended: { label: 'Ended', tone: '' },
}

const brand = (b: string | null) =>
  b
    ? ({ visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', american_express: 'Amex' }[b.toLowerCase()] ??
      b)
    : 'Card'

/**
 * Every subscription and the billing webhooks behind them (redesign of 2026-10-16): a status
 * strip that filters the list, filters for plan, billing and renewal, and the events on their
 * own tab. Changes to a subscription (refunds, cancellations for a customer) are made in Lemon
 * Squeezy; its webhooks bring them back here.
 */
export function SubscriptionsPage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'events' ? 'events' : 'subscriptions'

  const update = (next: Record<string, string | null>) =>
    setParams(
      (p) => {
        const merged = new URLSearchParams(p)
        for (const [k, v] of Object.entries(next)) {
          if (v === null || v === '') merged.delete(k)
          else merged.set(k, v)
        }
        if (!('page' in next)) merged.delete('page')
        return merged
      },
      { replace: true },
    )

  return (
    <>
      <PageTitle
        title="Subscriptions"
        description="Every plan and add-on subscription, its status and its next renewal. Refunds and cancellations on a customer’s behalf are made in Lemon Squeezy and show here when its webhook arrives."
      />
      {tab === 'events' ? (
        <EventsList params={params} update={update} onTab={() => setParams({})} />
      ) : (
        <SubscriptionsList params={params} update={update} onTab={() => setParams({ tab: 'events' })} />
      )}
    </>
  )
}

interface ListProps {
  params: URLSearchParams
  update: (next: Record<string, string | null>) => void
  onTab: () => void
}

function PageTabs({
  value,
  failed,
  onTab,
}: {
  value: 'subscriptions' | 'events'
  failed: number
  onTab: () => void
}) {
  return (
    <Tabs
      label="Subscriptions and billing events"
      value={value}
      onChange={(k) => {
        if (k !== value) onTab()
      }}
      tabs={[
        { key: 'subscriptions', label: 'Subscriptions' },
        failed > 0
          ? { key: 'events', label: 'Billing events', count: `${failed} failed`, alert: true }
          : { key: 'events', label: 'Billing events' },
      ]}
    />
  )
}

function SubscriptionsList({ params, update, onTab }: ListProps) {
  const navigate = useNavigate()
  const filters = {
    state: params.get('state') ?? '',
    kind: params.get('kind') ?? '',
    plan: params.get('plan') ?? '',
    interval: params.get('interval') ?? '',
    renews: params.get('renews') ?? '',
    search: params.get('search') ?? '',
    page: Number(params.get('page') ?? 1),
  }
  const [draft, setDraft] = useState(filters.search)
  const subscriptions = useSubscriptions(filters)
  const meta = subscriptions.data?.meta
  const states = meta?.states

  useEffect(() => {
    const t = setTimeout(() => {
      if (draft.trim() !== filters.search) update({ search: draft.trim() })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the typed search drives this
  }, [draft])

  return (
    <>
      {states && (
        <Pipe
          items={STATES.filter((s) => s.key !== 'on_trial' || states.on_trial.count > 0).map((s) => ({
            key: s.key,
            label: s.label,
            dot: s.dot,
            count: numberFmt.format(states[s.key].count),
            sub: s.sub(states[s.key].mrr),
          }))}
          value={(filters.state || null) as SubscriptionState | null}
          onChange={(k) => update({ state: k })}
        />
      )}
      <PageTabs value="subscriptions" failed={meta?.failed_events ?? 0} onTab={onTab} />
      <div className="fbar">
        <SearchField
          label="Search subscriptions"
          placeholder="Workspace, email or subscription ID"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <FilterChip
          label="Kind"
          icon={<Package aria-hidden="true" />}
          value={filters.kind}
          onChange={(v) => update({ kind: v })}
          options={[
            { value: 'plan', label: 'Plans' },
            { value: 'addon', label: 'Add-ons' },
          ]}
        />
        <FilterChip
          label="Plan"
          icon={<Layers aria-hidden="true" />}
          value={filters.plan}
          onChange={(v) => update({ plan: v })}
          options={(meta?.plans ?? []).map((p) => ({ value: p.key, label: p.name }))}
        />
        <FilterChip
          label="Billed"
          icon={<Repeat aria-hidden="true" />}
          value={filters.interval}
          onChange={(v) => update({ interval: v })}
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'annual', label: 'Yearly' },
          ]}
        />
        <FilterChip
          label="Renews"
          icon={<CalendarClock aria-hidden="true" />}
          value={filters.renews}
          onChange={(v) => update({ renews: v })}
          options={[
            { value: '7', label: 'In the next 7 days' },
            { value: '30', label: 'In the next 30 days' },
          ]}
        />
      </div>
      {subscriptions.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : subscriptions.isError ? (
        <div className="card">
          <ErrorState error={subscriptions.error} onRetry={() => void subscriptions.refetch()} />
        </div>
      ) : (
        <div className={cn('tw transition-opacity', subscriptions.isPlaceholderData && 'opacity-60')}>
          {subscriptions.data.data.length === 0 ? (
            <p className="empty-line" style={{ padding: 24 }}>
              No subscriptions match. Try another filter or search.
            </p>
          ) : (
            <table className="t">
              <caption className="sr-only">Subscriptions</caption>
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Subscription</th>
                  <th>Status</th>
                  <th className="r">A month</th>
                  <th>Next</th>
                  <th>Card</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.data.data.map((s) => (
                  <tr
                    key={s.id}
                    className={s.workspace ? 'click' : undefined}
                    onClick={
                      s.workspace ? () => void navigate(`/customers/${s.workspace?.id ?? ''}`) : undefined
                    }
                  >
                    <td>
                      <div className="who">
                        <Monogram name={s.workspace?.name ?? '?'} size="sm" />
                        <div>
                          {s.workspace ? (
                            <Link to={`/customers/${s.workspace.id}`} onClick={(e) => e.stopPropagation()}>
                              <b>{s.workspace.name}</b>
                            </Link>
                          ) : (
                            <b>No workspace</b>
                          )}
                          <small>{s.customer_email ?? '—'}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {s.kind === 'plan' && s.plan_key ? (
                        <PlanTag
                          plan={s.plan_key}
                          name={s.plan_name ?? s.plan_key}
                          yearly={s.interval === 'annual'}
                        />
                      ) : (
                        <b>{s.description}</b>
                      )}
                      <small className="mono">#{s.provider_id}</small>
                    </td>
                    <td>
                      <StateChip s={s} />
                      {s.test_mode && (
                        <span className="chip" style={{ marginLeft: 4 }}>
                          Test
                        </span>
                      )}
                    </td>
                    <td className="r num">{formatMoney(s.monthly_value)}</td>
                    <td className="m">{next(s)}</td>
                    <td className="m mono">
                      {s.card ? `${brand(s.card.brand)} ·· ${s.card.last_four}` : '—'}
                    </td>
                    <td className="m" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(s.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Foot data={subscriptions.data} noun="subscription" page={filters.page} update={update} />
        </div>
      )}
    </>
  )
}

function StateChip({ s }: { s: SubscriptionRow }) {
  const state = s.state ?? 'active'
  const chip = STATE_CHIP[state]
  return <span className={cn('chip', chip.tone)}>{s.refunded ? 'Refunded' : chip.label}</span>
}

/** When it next renews, retries or ends. */
function next(s: SubscriptionRow) {
  switch (s.state) {
    case 'past_due':
      return s.renews_at ? `Retrying, due ${formatDate(s.renews_at)}` : 'Retrying'
    case 'cancelling':
      return s.ends_at ? `Ends ${formatDate(s.ends_at)}` : 'Ends at period end'
    case 'ended':
      return s.ends_at ? `Ended ${formatDate(s.ends_at)}` : 'Ended'
    case 'paused':
      return 'Paused'
    default:
      return s.renews_at ? `Renews ${formatDate(s.renews_at)}` : '—'
  }
}

function EventsList({ params, update, onTab }: ListProps) {
  const me = useMe()
  const owner = me.data?.role === 'owner'
  const status = (params.get('status') ?? '') as BillingEventStatus | ''
  const page = Number(params.get('page') ?? 1)
  const events = useBillingEvents(status, page)
  const retry = useRetryBillingEvent()
  const failed = events.data?.meta.failed_events ?? 0

  return (
    <>
      <PageTabs value="events" failed={failed} onTab={onTab} />
      <div className="fbar">
        <Segmented
          label="Status"
          value={status}
          options={[
            { value: '', label: 'All' },
            { value: 'failed', label: 'Failed' },
            { value: 'pending', label: 'Waiting' },
            { value: 'ignored', label: 'Ignored' },
            { value: 'processed', label: 'Applied' },
          ]}
          onChange={(v) => update({ status: v })}
        />
      </div>
      {events.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : events.isError ? (
        <div className="card">
          <ErrorState error={events.error} onRetry={() => void events.refetch()} />
        </div>
      ) : (
        <div className="tw">
          {events.data.data.length === 0 ? (
            <p className="empty-line" style={{ padding: 24 }}>
              No billing events. Lemon Squeezy’s webhooks are listed here as they arrive.
            </p>
          ) : (
            <table className="t">
              <caption className="sr-only">Billing events</caption>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Customer</th>
                  <th>Received</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {events.data.data.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <span className="mono">{e.event}</span>
                      <small>
                        #{e.id}
                        {e.object && ` · ${e.object.type} ${e.object.id}`}
                        {e.attempts > 1 && ` · ${e.attempts} attempts`}
                      </small>
                      {e.error && (
                        <small style={{ color: 'var(--bad)', whiteSpace: 'normal' }}>{e.error}</small>
                      )}
                    </td>
                    <td>
                      {e.workspace_id ? (
                        <Link to={`/customers/${e.workspace_id}`}>Open customer</Link>
                      ) : (
                        <span className="m">—</span>
                      )}
                    </td>
                    <td className="m" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.received_at)}
                    </td>
                    <td>
                      <span
                        className={cn(
                          'chip',
                          e.status === 'failed'
                            ? 'bad'
                            : e.status === 'pending'
                              ? 'warn'
                              : e.status === 'processed'
                                ? 'ok'
                                : '',
                        )}
                      >
                        {e.status === 'failed'
                          ? 'Couldn’t process'
                          : e.status === 'pending'
                            ? 'Waiting'
                            : label(e.status)}
                      </span>
                      {e.test_mode && (
                        <span className="chip" style={{ marginLeft: 4 }}>
                          Test
                        </span>
                      )}
                    </td>
                    <td className="r">
                      {owner && e.status === 'failed' && (
                        <button
                          type="button"
                          className="btn sm"
                          disabled={retry.isPending}
                          onClick={() =>
                            retry.mutate(e.id, {
                              onSuccess: () => toast.success('Sent to be applied again.'),
                              onError: (err) => toast.error(errorMessage(err)),
                            })
                          }
                        >
                          Run again
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Foot data={events.data} noun="event" page={page} update={update} />
        </div>
      )}
    </>
  )
}

function Foot({
  data,
  noun,
  page,
  update,
}: {
  data: Paginated<unknown>
  noun: string
  page: number
  update: (next: Record<string, string | null>) => void
}) {
  const { meta } = data
  if (data.data.length === 0) return null
  return (
    <div className="t-foot">
      <span>
        {meta.last_page > 1 ? `Page ${meta.current_page} of ${meta.last_page} · ` : ''}
        {numberFmt.format(meta.total)} {meta.total === 1 ? noun : `${noun}s`}
      </span>
      {meta.last_page > 1 && (
        <span className="acts">
          <button
            type="button"
            className="btn sm ghost"
            disabled={page <= 1}
            onClick={() => update({ page: String(page - 1) })}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn sm ghost"
            disabled={page >= meta.last_page}
            onClick={() => update({ page: String(page + 1) })}
          >
            Next
          </button>
        </span>
      )}
    </div>
  )
}
